import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Heading } from '../common/form/form-parts';
import * as Validator from 'validator';
import { touchWebClient } from 'tradera/utils/http';
import { Trans, useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import GoogleTagManager from '../../services/google-tag-manager';
import EventEmitter from '../../helpers/event-emitter';
import '../../services/form-tracker';
import { FormError } from '../common/form/form-error';
import { FormFooter } from 'tradera/apps/registration/components/common/form/form-footer';
import FormHeader, {
    SwedenCountryCode
} from 'tradera/apps/registration/components/common/form/form-header';
import { getAvailableUserRegistrationCountries } from 'tradera/apps/registration/helpers/get-available-user-registration-countries';
import { KountBlockedMessage } from 'tradera/apps/registration/components/common/kount-blocked-message';
import { FormCompleted } from 'tradera/apps/registration/components/common/form/form-completed';
import {
    getAcceptsMarketing,
    setAcceptsMarketing,
    triggerUserSignedUpEvent
} from 'tradera/apps/registration/helpers/blueshift';
import { SelectCountry } from '../common/form/select-country';
import { Button } from 'tradera/components/atoms/button/button';
import { waitForCountSessionId } from 'tradera/apps/registration/components/common/kount-session-id-awaiter';

export const SwedishCompanyForm = ({ antiForgeryToken }) => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const availableCountries = getAvailableUserRegistrationCountries(t);

    useEffect(() => {
        EventEmitter.dispatch('Form:Loaded', {
            name: 'swedish-company',
            step: 1
        });
    }, []);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm({
        mode: 'onBlur'
    });

    const [successfulSubmissionData, setSuccessfulSubmissionData] =
        useState(null);
    const [serverErrors, setServerErrors] = useState({});

    const validateAsync = async (url, value) => {
        try {
            await touchWebClient.post(
                url,
                {
                    value: value.trim()
                },
                {
                    headers: {
                        ['X-Xsrf-Token']: antiForgeryToken
                    }
                }
            );

            return { success: true };
        } catch (err) {
            if (
                err.response &&
                err.response.data &&
                err.response.status === 400
            ) {
                return { success: false, error: err.response.data };
            }

            throw err;
        }
    };

    const setServerValidationError = (error, field) => {
        if (error) {
            setServerErrors({
                ...serverErrors,
                [field]: error.code
            });
        } else if (!error) {
            const _serverErrors = { ...serverErrors };
            delete _serverErrors[field];
            setServerErrors(_serverErrors);
        }
    };

    const validateEmail = async (v) => {
        const { success, error } = await validateAsync(
            '/register/api/validate/email',
            v
        );

        setServerValidationError(error, 'emailAddress');
        return success;
    };

    const validateOrganizationNumber = async (v) => {
        const { success, error } = await validateAsync(
            '/register/api/validate/organization-number',
            v
        );

        setServerValidationError(error, 'organizationNumber');
        return success;
    };

    const validateAlias = async (v) => {
        const { success, error } = await validateAsync(
            '/register/api/validate/alias',
            v
        );

        setServerValidationError(error, 'alias');
        return success;
    };

    const onSubmit = async (data) => {
        try {
            const sessionId = await waitForCountSessionId();
            const result = await touchWebClient.post(
                '/api/register/swedish-company',
                {
                    ...data,
                    sessionId
                },
                {
                    headers: {
                        ['X-Xsrf-Token']: antiForgeryToken
                    }
                }
            );

            window.location.hash = 'confirm';
            EventEmitter.dispatch('Form:Submit:Success', result.data);

            GoogleTagManager.pushToDataLayer({
                event: 'user-signup-success',
                'member-id': result.data.memberId
            });
            GoogleTagManager.trackEvent({
                category: 'Login and Signup',
                action: 'Signup confirm - swedish company'
            });
            setSuccessfulSubmissionData({
                emailAddress: result.data.emailAddress,
                redirectLink: result.data.redirectLink
            });
            triggerUserSignedUpEvent(
                result.data.memberId,
                result.data.emailAddress
            );
        } catch (err) {
            if (err.response && err.response.data && err.response.data.errors) {
                setServerErrors(
                    err.response.data.errors.reduce((serverErrors, error) => {
                        serverErrors[error.field] = error.code;
                        return serverErrors;
                    }, {})
                );
                EventEmitter.dispatch('Form:Submit:Failure', {
                    errors: err.response.data.errors
                });
            }
        }
    };

    const invalidFieldClassName = 'is-invalid';

    return (
        <>
            {!successfulSubmissionData && (
                <>
                    <FormHeader showLanguageMenu={true} />
                    <KountBlockedMessage />
                    <form className="mb-3" onSubmit={handleSubmit(onSubmit)}>
                        <Heading>
                            {t('registration_company_sign_up_heading')}
                        </Heading>
                        <SelectCountry
                            availableCountries={availableCountries}
                            onCountrySelected={(selectedValue) => {
                                window.location.href = `${toLocalizedUrl(
                                    '/register/foreign-company'
                                )}?countryCode=${selectedValue}`;
                            }}
                            selectedCountryCodeIso2={SwedenCountryCode}
                            className="mb-1"
                        />
                        <div className="mb-1">
                            <input
                                placeholder={t(
                                    'registration_placeholder_orgnr'
                                )}
                                type="text"
                                className={`form-control px-1 py-2 ${
                                    errors.organizationNumber
                                        ? invalidFieldClassName
                                        : ''
                                }`}
                                {...register('organizationNumber', {
                                    required: t(
                                        'registration_validation_required_organizationnumber'
                                    ),
                                    validate: {
                                        isValid: validateOrganizationNumber
                                    }
                                })}
                            />
                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="organizationNumber"
                            />
                        </div>
                        <div className="mb-1">
                            <input
                                type="text"
                                className={`form-control px-1 py-2 ${
                                    errors.companyName
                                        ? invalidFieldClassName
                                        : ''
                                }`}
                                placeholder={t(
                                    'registration_placeholder_company_name'
                                )}
                                {...register('companyName', {
                                    required: t(
                                        'registration_validation_required_companyname'
                                    ),
                                    minLength: {
                                        value: 2,
                                        message: t(
                                            'registration_validation_minlength_companyname'
                                        )
                                    },
                                    maxLength: {
                                        value: 40,
                                        message: t(
                                            'registration_validation_maxlength_companyname'
                                        )
                                    },
                                    pattern: {
                                        value: /^[0-9\u0041-\u024F -:]+$/,
                                        message: t(
                                            'registration_validation_invalid_characters'
                                        )
                                    }
                                })}
                            />

                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="companyName"
                            />
                        </div>
                        <div className="mb-1">
                            <input
                                placeholder={t(
                                    'registration_placeholder_email'
                                )}
                                type="email"
                                className={`form-control px-1 py-2 ${
                                    errors.emailAddress
                                        ? invalidFieldClassName
                                        : ''
                                }`}
                                {...register('emailAddress', {
                                    required: t(
                                        'registration_validation_required_email'
                                    ),
                                    minLength: {
                                        value: 5,
                                        message: t(
                                            'registration_validation_invalid_length_email'
                                        )
                                    },
                                    maxLength: {
                                        value: 255,
                                        message: t(
                                            'registration_validation_maxlength_email'
                                        )
                                    },
                                    validate: {
                                        isValid: validateEmail
                                    }
                                })}
                            />
                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="emailAddress"
                            />
                        </div>

                        <div className="mb-1">
                            <input
                                placeholder={t(
                                    'registration_placeholder_street'
                                )}
                                className={`form-control px-1 py-2 ${
                                    errors.streetName
                                        ? invalidFieldClassName
                                        : ''
                                }`}
                                {...register('streetName', {
                                    required: t(
                                        'registration_validation_required_streetname'
                                    ),
                                    minLength: {
                                        value: 1,
                                        message: t(
                                            'registration_validation_minlength_streetname'
                                        )
                                    },
                                    maxLength: {
                                        value: 50,
                                        message: t(
                                            'registration_validation_maxlength_streetname'
                                        )
                                    },
                                    pattern: {
                                        value: /^[0-9\u0041-\u024F -:]+$/,
                                        message: t(
                                            'registration_validation_invalid_characters'
                                        )
                                    }
                                })}
                            />
                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="streetName"
                            />
                        </div>

                        <div className="d-flex w-100 mb-1">
                            <div className="mr-1 w-50">
                                <input
                                    type="tel"
                                    className={`form-control px-1 py-2 ${
                                        errors.zipCode
                                            ? invalidFieldClassName
                                            : ''
                                    }`}
                                    placeholder={t(
                                        'registration_placeholder_zip_code'
                                    )}
                                    {...register('zipCode', {
                                        required: t(
                                            'registration_validation_required_zipcode'
                                        ),
                                        minLength: {
                                            value: 4,
                                            message: t(
                                                'registration_validation_invalid_zipcode'
                                            )
                                        },
                                        maxLength: {
                                            value: 8,
                                            message: t(
                                                'registration_validation_invalid_zipcode'
                                            )
                                        },
                                        validate: (value) => {
                                            const rawValue = value.replace(
                                                /[ -]/g,
                                                ''
                                            );
                                            return (
                                                (Validator.isNumeric(
                                                    rawValue
                                                ) &&
                                                    rawValue.length === 5) ||
                                                t(
                                                    'registration_validation_invalid_zipcode'
                                                )
                                            );
                                        }
                                    })}
                                />

                                <FormError
                                    serverErrors={serverErrors}
                                    errors={errors}
                                    name="zipCode"
                                />
                            </div>
                            <div className="w-50">
                                <input
                                    type="text"
                                    className={`form-control px-1 py-2 ${
                                        errors.town ? invalidFieldClassName : ''
                                    }`}
                                    placeholder={t(
                                        'registration_placeholder_town'
                                    )}
                                    {...register('town', {
                                        required: t(
                                            'registration_validation_required_town'
                                        ),
                                        minLength: {
                                            value: 2,
                                            message: t(
                                                'registration_validation_minlength_town'
                                            )
                                        },
                                        maxLength: {
                                            value: 36,
                                            message: t(
                                                'registration_validation_maxlength_town'
                                            )
                                        },
                                        pattern: {
                                            value: /^[\u0041-\u024F -:]+$/,
                                            message: t(
                                                'registration_validation_invalid_town'
                                            )
                                        }
                                    })}
                                />
                                <FormError
                                    serverErrors={serverErrors}
                                    errors={errors}
                                    name="town"
                                />
                            </div>
                        </div>

                        <div className="d-flex w-100 mb-1">
                            <div className="mr-1 w-50">
                                <input
                                    type="text"
                                    className={`form-control px-1 py-2 ${
                                        errors.contactFirstName
                                            ? invalidFieldClassName
                                            : ''
                                    }`}
                                    placeholder={t(
                                        'registration_placeholder_first_name'
                                    )}
                                    {...register('contactFirstName', {
                                        required: t(
                                            'registration_validation_required_firstname'
                                        ),
                                        minLength: {
                                            value: 2,
                                            message: t(
                                                'registration_validation_minlength_firstname'
                                            )
                                        },
                                        maxLength: {
                                            value: 30,
                                            message: t(
                                                'registration_validation_maxlength_firstname'
                                            )
                                        },
                                        pattern: {
                                            value: /^[\u0041-\u024F -:]+$/,
                                            message: t(
                                                'registration_validation_invalid_characters'
                                            )
                                        }
                                    })}
                                />

                                <FormError
                                    serverErrors={serverErrors}
                                    errors={errors}
                                    name="contactFirstName"
                                />
                            </div>
                            <div className="w-50">
                                <input
                                    type="text"
                                    className={`form-control px-1 py-2 ${
                                        errors.contactLastName
                                            ? invalidFieldClassName
                                            : ''
                                    }`}
                                    placeholder={t(
                                        'registration_placeholder_last_name'
                                    )}
                                    {...register('contactLastName', {
                                        required: t(
                                            'registration_validation_required_lastname'
                                        ),
                                        minLength: {
                                            value: 2,
                                            message: t(
                                                'registration_validation_minlength_lastname'
                                            )
                                        },
                                        maxLength: {
                                            value: 30,
                                            message: t(
                                                'registration_validation_maxlength_lastname'
                                            )
                                        },
                                        pattern: {
                                            value: /^[\u0041-\u024F -:]+$/,
                                            message: t(
                                                'registration_validation_invalid_characters'
                                            )
                                        }
                                    })}
                                />
                                <FormError
                                    serverErrors={serverErrors}
                                    errors={errors}
                                    name="contactLastName"
                                />
                            </div>
                        </div>

                        <div className="mb-1">
                            <input
                                type="tel"
                                className={`form-control px-1 py-2 ${
                                    errors.phoneNumber
                                        ? invalidFieldClassName
                                        : ''
                                }`}
                                placeholder="Mobilnummer"
                                {...register('phoneNumber', {
                                    required: t(
                                        'registration_validation_required_phonenumber'
                                    ),
                                    pattern: {
                                        value: /^(\+?\d{2})?[\d\- ]+$/,
                                        message: t(
                                            'registration_validation_invalid_phonenumber'
                                        )
                                    }
                                })}
                            />

                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="phoneNumber"
                            />
                        </div>

                        <div className="mb-3">
                            <input
                                placeholder={t(
                                    'registration_placeholder_username'
                                )}
                                type="text"
                                className={`form-control px-1 py-2 ${
                                    errors.alias ? invalidFieldClassName : ''
                                }`}
                                {...register('alias', {
                                    required: t(
                                        'registration_validation_required_alias'
                                    ),
                                    minLength: {
                                        value: 2,
                                        message: t(
                                            'registration_validation_minlength_alias'
                                        )
                                    },
                                    maxLength: {
                                        value: 50,
                                        message: t(
                                            'registration_validation_maxlength_alias'
                                        )
                                    },
                                    pattern: {
                                        value: /^[0-9\u0041-\u024F -:]+$/,
                                        message: t(
                                            'registration_validation_invalid_characters'
                                        )
                                    },
                                    validate: validateAlias
                                })}
                            />
                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="alias"
                            />
                        </div>

                        <div className="mb-1">
                            <div className="form-check mb-0 align-items-center">
                                <input
                                    id="terms"
                                    type="checkbox"
                                    className={`form-check-input position-relative mr-1 ${
                                        errors.terms
                                            ? invalidFieldClassName
                                            : ''
                                    }`}
                                    {...register('terms', {
                                        required: t(
                                            'registration_validation_required_terms'
                                        )
                                    })}
                                />
                                <label htmlFor="terms" className="mb-0">
                                    <Trans
                                        i18nKey="registration_terms_label_Trans"
                                        components={{
                                            a: (
                                                // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content
                                                <a
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="link"
                                                />
                                            )
                                        }}
                                    />
                                </label>
                            </div>
                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="terms"
                            />
                        </div>

                        <div className="mb-1">
                            <div className="form-check mb-0 align-items-center">
                                <input
                                    id="marketing"
                                    type="checkbox"
                                    className={`form-check-input position-relative mr-1`}
                                    onChange={(e) =>
                                        setAcceptsMarketing(e.target.checked)
                                    }
                                    defaultChecked={getAcceptsMarketing()}
                                    {...register('marketing', {})}
                                />
                                <label htmlFor="marketing" className="mb-0">
                                    {t('registration_marketing_label')}
                                </label>
                            </div>
                            <FormError
                                serverErrors={serverErrors}
                                errors={errors}
                                name="marketing"
                            />
                        </div>

                        <div className="mb-1">
                            {t('registration_company_authorizedRegistration')}
                        </div>

                        <div className="mt-2">
                            <Button theme="primary" fluid type="submit">
                                {t('registration_sign_up_button')}
                            </Button>
                        </div>
                    </form>
                </>
            )}
            {successfulSubmissionData && (
                <>
                    <div />
                    <FormCompleted
                        redirectLink={successfulSubmissionData.redirectLink}
                        emailAddress={successfulSubmissionData.emailAddress}
                        accountCreatedInfoText={t(
                            'registration_company_account_created_info'
                        )}
                        sendPasswordText={t(
                            'registration_company_account_created_send_password'
                        )}
                        registrationProblemsText={
                            <Trans
                                i18nKey="registration_problems_swedish_company_Trans"
                                // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content
                                components={{ a: <a /> }}
                            />
                        }
                    />
                </>
            )}
            <FormFooter
                registerLink={'/register'}
                isCompanyLink={false}
                logoOnly={!!successfulSubmissionData}
            />
        </>
    );
};
